/**
 * Página CrmDealsPipeline - Visualização Kanban dos Deals
 * 
 * Características:
 * - Lê pipeline da URL via query param ?id=
 * - Filtra pipelines por workspace_id (não mistura workspaces)
 * - Header com título da pipeline + busca expansível + filtros rápidos
 * - Tabs para navegação entre pipelines do mesmo workspace
 * - CRUD de pipelines via DealPipelineTabs
 * 
 * Versão: 4.0.0
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { CrmDeal, DealFilters } from '@/hooks/useCrmDeals';
import { DealKanbanBoard } from '@/components/crm/deals/DealKanbanBoard';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { DealPipelineTabs, PipelineTab } from '@/components/crm/deals/DealPipelineTabs';
import { DealKanbanFilters } from '@/components/crm/deals/DealKanbanFilters';
import { BulkTransferDialog } from '@/components/tenant/transfers/BulkTransferDialog';
import { AutomationKanbanModal } from '@/components/crm/deals/AutomationKanbanModal';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { ActivityDetailsModalPremium } from '@/components/crm/activities/modal/ActivityDetailsModalPremium';
import { useDealPipelines } from '@/hooks/useDealPipelines';
import { usePhaseAutomations } from '@/hooks/usePhaseAutomations';
import { Skeleton } from '@/components/ui/skeleton';
import { logger } from '@/lib/logger';

/**
 * Página principal do Pipeline Kanban de Deals
 */
export default function CrmDealsPipeline() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  
  // Estados principais
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<CrmDeal | null>(null);
  const [defaultPhaseId, setDefaultPhaseId] = useState<string | undefined>();
  
  // Estado para modal de detalhes do deal
  const [viewDealId, setViewDealId] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // v3.5.0: o modal de sucesso pós-criação passou a ser montado pelo DealFormDialog

  
  // Estado para filtros do Kanban
  const [filters, setFilters] = useState<DealFilters>({});
  
  // Estado para modal de transferência em massa
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  
  // Estado para modal de automações
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  
  // Estado para modal de chat (aberto via ícone de balão no DealCard)
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);

  // Estado para modal de cliente (aberto via nome do contato no DealCard)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);

  // Estado para modal 360º do contato (aberto via nome do contato quando o
  // negócio ainda não tem cliente vinculado)
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  // v2.5.0: modal de atividade aberto via badge de checklist no DealCard
  const [activityModalDealId, setActivityModalDealId] = useState<string | null>(null);
  const [activityModalOpen, setActivityModalOpen] = useState(false);

  // Hook de CRUD de pipelines
  const { createPipeline, updatePipeline, deletePipeline, reorderPipelines } = useDealPipelines();
  
  // Hook para detectar automações por fase (para indicador visual)
  const { totalAutomations } = usePhaseAutomations(selectedPipelineId);
  
  // ==========================================================================
  // CONTROLE DE PERMISSÃO PARA ÍCONES DE AUTOMAÇÃO E TRANSFERÊNCIA EM MASSA
  // Admin: sempre visível | Supervisor: sempre visível para transferência
  // Agent: nunca visível
  // ==========================================================================
  const { isAdmin, isSupervisor, canView, canCreate, canEdit } = usePermissions();
  const canViewAutomations = useMemo(() => {
    if (isAdmin) return true;
    return canView('flows') || canView('routing');
  }, [isAdmin, canView]);
  
  // Permissão para transferência em massa: Admin ou Supervisor
  const canBulkTransfer = isAdmin || isSupervisor;

  // Pipeline ID da URL
  const pipelineIdFromUrl = searchParams.get('id');

  // ==========================================================================
  // BOOTSTRAP SERVER-SIDE (v1.0.0)
  // Substitui 3 SELECTs diretos por 1 RPC `get_crm_kanban_bootstrap` que
  // retorna em uma única chamada:
  //   - current_*: pipeline resolvido (id da URL OU fallback default do tenant)
  //   - workspace_pipelines: lista para as abas do workspace correspondente
  // Mantém o `useEffect` que sincroniza a URL quando o pipeline veio do fallback.
  // ==========================================================================
  const { data: bootstrap, isLoading: bootstrapLoading } = useQuery({
    queryKey: ['crm-kanban-bootstrap', currentTenant?.id, pipelineIdFromUrl],
    queryFn: async () => {
      if (!currentTenant?.id) return null;

      const { data, error } = await supabase.rpc('get_crm_kanban_bootstrap', {
        p_pipeline_id: pipelineIdFromUrl ?? null,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('CrmDealsPipeline', 'Falha no bootstrap: ' + error.message);
        throw error;
      }

      // RPC retorna sempre 1 linha (mesmo quando tenant não tem pipelines)
      const row = Array.isArray(data) ? data[0] : data;
      return row ?? null;
    },
    enabled: !!currentTenant?.id,
  });

  // Deriva os mesmos shapes que o restante do componente já consome.
  // Mantém formato { id, name, workspace_id, is_default } para `currentPipeline`
  // e [{ id, name, is_default }] para `pipelines`.
  const currentPipeline = bootstrap?.current_pipeline_id
    ? {
        id: bootstrap.current_pipeline_id as string,
        name: bootstrap.current_pipeline_name as string,
        workspace_id: bootstrap.current_workspace_id as string,
        is_default: bootstrap.current_is_default as boolean,
      }
    : null;

  const pipelines = (bootstrap?.workspace_pipelines as Array<{
    id: string;
    name: string;
    color: string | null; // v1.1.0: cor personalizada (NULL = fallback determinístico)
    is_default: boolean;
  }> | undefined) ?? [];

  const currentPipelineLoading = bootstrapLoading;
  const pipelinesLoading = bootstrapLoading;

  // Atualiza estados locais a partir do bootstrap
  useEffect(() => {
    if (currentPipeline?.workspace_id) {
      setActiveWorkspaceId(currentPipeline.workspace_id);
      setSelectedPipelineId(currentPipeline.id);
    }
  }, [currentPipeline?.id, currentPipeline?.workspace_id]);

  // Se não havia ?id= na URL e o bootstrap resolveu via fallback, sincroniza URL
  useEffect(() => {
    if (!pipelineIdFromUrl && currentPipeline?.id) {
      setSearchParams({ id: currentPipeline.id }, { replace: true });
    }
  }, [pipelineIdFromUrl, currentPipeline?.id]);

  // Quando troca pipeline no dropdown, atualiza URL
  const handlePipelineChange = (pipelineId: string) => {
    setSelectedPipelineId(pipelineId);
    setSearchParams({ id: pipelineId });
  };

  // Abre form para adicionar deal em fase específica
  const handleAddDeal = (phaseId: string) => {
    setDefaultPhaseId(phaseId);
    setEditingDeal(null);
    setFormOpen(true);
  };

  // Abre form para editar deal
  const handleEditDeal = (deal: CrmDeal) => {
    setEditingDeal(deal);
    setDefaultPhaseId(undefined);
    setFormOpen(true);
  };

  // Abre modal de detalhes do deal
  const handleViewDeal = (deal: CrmDeal) => {
    setViewDealId(deal.id);
    setViewModalOpen(true);
  };

  // Abre modal de chat com a conversa específica (via ícone de balão no DealCard)
  const handleOpenChat = useCallback((conversationId: string) => {
    setChatConversationId(conversationId);
    setChatModalOpen(true);
  }, []);

  // Abre modal de cliente (via clique no nome do contato no DealCard)
  const handleViewCustomer = useCallback((customerId: string) => {
    setSelectedCustomerId(customerId);
    setCustomerModalOpen(true);
  }, []);

  // Abre modal 360º do contato (via clique no nome quando não há cliente).
  // v2.6.0: guarda o preview enviado pelo card para a ficha abrir preenchida
  const [selectedContactPreview, setSelectedContactPreview] = useState<{
    id: string;
    name: string | null;
    phone?: string | null;
    email?: string | null;
    avatar_url?: string | null;
  } | null>(null);
  const handleViewContact = useCallback((contactId: string, preview?: {
    id: string;
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    avatar_url?: string | null;
  }) => {
    setSelectedContactId(contactId);
    // Normaliza o preview para o formato ContactPreview (name obrigatório)
    setSelectedContactPreview(preview ? { ...preview, name: preview.name ?? null } : null);
    setCustomerModalOpen(true);
  }, []);

  // Fecha form
  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingDeal(null);
    setDefaultPhaseId(undefined);
  };

  // Handler para novo negócio (sem fase específica)
  const handleNewDeal = () => {
    setEditingDeal(null);
    setDefaultPhaseId(undefined);
    setFormOpen(true);
  };

  // Handler para refresh do Kanban (v4.0.1 - corrigido para invalidar também os stats)
  const handleRefresh = () => {
    if (selectedPipelineId) {
      // Invalida os cards das colunas
      queryClient.invalidateQueries({
        queryKey: ['kanban-column', selectedPipelineId],
        exact: false,
      });
      // Invalida os contadores das fases para sincronizar com os cards
      queryClient.invalidateQueries({
        queryKey: ['pipeline-phase-stats', selectedPipelineId],
      });
    }
  };

  // Nome da pipeline selecionada
  const selectedPipelineName = useMemo(() => {
    if (!selectedPipelineId || !pipelines) return '';
    const pipeline = pipelines.find(p => p.id === selectedPipelineId);
    return pipeline?.name || currentPipeline?.name || '';
  }, [selectedPipelineId, pipelines, currentPipeline]);

  // Loading state
  const isLoading = currentPipelineLoading || (pipelinesLoading && !!activeWorkspaceId);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <div className="flex gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-96 w-72" />)}
        </div>
      </div>
    );
  }

  if (!pipelines || pipelines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <LayoutGrid className="h-16 w-16 text-muted-foreground" />
        <h2 className="text-xl font-semibold">Nenhum pipeline configurado</h2>
        <p className="text-muted-foreground">Crie um pipeline para começar a gerenciar seus negócios.</p>
      </div>
    );
  }

  // Cores para pipelines (roda entre as disponíveis)
  const PIPELINE_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  ];

  /**
   * Cor determinística por pipeline (v4.1.0)
   * Antes a cor vinha do índice na lista, então mudava ao reordenar as abas.
   * Agora é derivada do próprio `id`, permanecendo a mesma em qualquer ordem.
   */
  const getPipelineColor = (id: string): string => {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash + id.charCodeAt(i)) % PIPELINE_COLORS.length;
    }
    return PIPELINE_COLORS[hash];
  };

  // Converte pipelines para formato esperado pelo DealPipelineTabs
  // v1.1.0: prioriza a cor salva no banco; hash do id vira apenas fallback
  const pipelineTabs: PipelineTab[] = (pipelines || []).map((p) => ({
    id: p.id,
    name: p.name,
    color: p.color || getPipelineColor(p.id),
    is_default: p.is_default,
  }));


  // Handlers de CRUD de pipelines
  const handleCreatePipeline = async (name: string, color: string) => {
    // Valida workspace ativo antes de chamar mutation
    if (!activeWorkspaceId) {
      logger.error('handleCreatePipeline', 'Workspace ativo não encontrado');
      return;
    }
    
    const result = await createPipeline.mutateAsync({ name, color, workspaceId: activeWorkspaceId });
    if (result?.id) {
      handlePipelineChange(result.id);
    }
  };

  const handleUpdatePipeline = async (id: string, updates: Partial<PipelineTab>) => {
    await updatePipeline.mutateAsync({ id, updates });
  };

  const handleDeletePipeline = async (id: string) => {
    await deletePipeline.mutateAsync(id);
    // Se excluiu o pipeline selecionado, seleciona o primeiro disponível
    if (id === selectedPipelineId && pipelines && pipelines.length > 1) {
      const remaining = pipelines.filter(p => p.id !== id);
      if (remaining.length > 0) {
        handlePipelineChange(remaining[0].id);
      }
    }
    return true;
  };

  /**
   * Persiste a nova ordem das abas de pipeline do workspace ativo.
   */
  const handleReorderPipelines = async (pipelineIds: string[]) => {
    if (!activeWorkspaceId) {
      logger.error('handleReorderPipelines', 'Workspace ativo não encontrado');
      return;
    }
    await reorderPipelines.mutateAsync({ workspaceId: activeWorkspaceId, pipelineIds });
  };



  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 overflow-hidden">
      {/* Header: Linha 1 - Título + Busca + Filtros + Novo Negócio - Permite scroll interno no mobile */}
      <div className="px-3 sm:px-4 py-3 border-b shrink-0 space-y-2 sm:space-y-3 w-full max-w-full">
        <DealKanbanFilters
          pipelineName={selectedPipelineName}
          filters={filters}
          onFiltersChange={setFilters}
          onNewDeal={handleNewDeal}
          onRefresh={handleRefresh}
          onBulkTransfer={canBulkTransfer ? () => setBulkTransferOpen(true) : undefined}
          // Automações: só mostra se usuário tem permissão
          onAutomationRules={canViewAutomations ? () => setAutomationModalOpen(true) : undefined}
          hasAutomations={canViewAutomations && totalAutomations > 0}
        />
        
        {/* Modal de Transferência em Massa */}
        <BulkTransferDialog
          open={bulkTransferOpen}
          onOpenChange={setBulkTransferOpen}
          context="deals"
          showDealOptions={true}
        />
        
        {/* Modal de Kanban de Automações */}
        <AutomationKanbanModal
          open={automationModalOpen}
          onOpenChange={setAutomationModalOpen}
          pipelineId={selectedPipelineId}
          pipelineName={selectedPipelineName}
        />
        
        {/* Linha 2 - Tabs de Pipelines (somente do workspace) */}
        <DealPipelineTabs
          pipelines={pipelineTabs}
          selectedPipelineId={selectedPipelineId}
          onSelectPipeline={handlePipelineChange}
          onCreatePipeline={handleCreatePipeline}
          onUpdatePipeline={handleUpdatePipeline}
          onDeletePipeline={handleDeletePipeline}
          // Oculta botão "Novo Pipeline" para usuários sem permissão granular
          canCreate={canCreate('crm_pipelines')}
          // Reordenação das abas somente para quem pode editar pipelines
          onReorderPipelines={canEdit('crm_pipelines') ? handleReorderPipelines : undefined}
        />
      </div>

      {/* Kanban Board com filtros */}
      {selectedPipelineId && (
        <div className="flex-1 min-h-0 overflow-hidden">
          <DealKanbanBoard
            pipelineId={selectedPipelineId}
            workspaceId={activeWorkspaceId}
            filters={filters}
            onAddDeal={handleAddDeal}
            onEditDeal={handleEditDeal}
            onViewDeal={handleViewDeal}
            onAutomationClick={() => setAutomationModalOpen(true)}
            onOpenChat={handleOpenChat}
            onViewCustomer={handleViewCustomer}
            onViewContact={handleViewContact}
            onOpenActivity={(dealId) => {
              setActivityModalDealId(dealId);
              setActivityModalOpen(true);
            }}
          />
        </div>
      )}

      {/* Modal de Chat — aberto via ícone de balão no DealCard */}
      <ChatConversationModal
        conversationId={chatConversationId}
        open={chatModalOpen}
        onOpenChange={(open) => {
          setChatModalOpen(open);
          if (!open) setChatConversationId(null);
        }}
      />

      {/* Dialog de formulário */}
      <DealFormDialog
        open={formOpen}
        onOpenChange={handleCloseForm}
        deal={editingDeal}
        defaultPipelineId={selectedPipelineId || undefined}
        defaultPhaseId={defaultPhaseId}
        defaultWorkspaceId={activeWorkspaceId || undefined}
        /* v3.5.0: o modal de sucesso agora vive dentro do DealFormDialog;
           aqui só informamos como abrir o negócio (modal premium). */
        onViewDeal={(dealId) => {
          setViewDealId(dealId);
          setViewModalOpen(true);
        }}
        onSuccess={() => {
          if (selectedPipelineId) {
            queryClient.invalidateQueries({
              queryKey: ['kanban-column', selectedPipelineId],
              exact: false,
            });
          }
        }}
      />



      {/* Modal premium de detalhes do deal */}
      <DealDetailsModalPremium
        dealId={viewDealId}
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
        onEdit={(deal) => {
          setViewModalOpen(false);
          handleEditDeal(deal);
        }}
        onDeleted={() => {
          if (selectedPipelineId) {
            queryClient.invalidateQueries({
              queryKey: ['kanban-column', selectedPipelineId],
              exact: false,
            });
          }
        }}
        onOpenConversation={handleOpenChat}
      />

      {/* Modal de cliente — aberto via clique no nome do contato no DealCard */}
      <CustomerDetailsModalPremium
        customerId={selectedCustomerId}
        contactId={selectedContactId}
        // v2.6.0: preview do contato vindo do card do funil
        contactPreview={selectedContactPreview}
        open={customerModalOpen}
        onOpenChange={(open) => {
          setCustomerModalOpen(open);
          if (!open) {
            setSelectedCustomerId(null);
            setSelectedContactId(null);
            setSelectedContactPreview(null);
          }
        }}
      />

      {/* v2.5.0: Modal de atividade — aberto pelo badge de checklist no DealCard.
          onOpenDeal permite alternar para o modal premium do negócio. */}
      <ActivityDetailsModalPremium
        dealId={activityModalDealId}
        open={activityModalOpen}
        onOpenChange={(open) => {
          setActivityModalOpen(open);
          if (!open) setActivityModalDealId(null);
        }}
        onOpenDeal={(dealId) => {
          setActivityModalOpen(false);
          setActivityModalDealId(null);
          setViewDealId(dealId);
          setViewModalOpen(true);
        }}
      />
    </div>
  );
}
